import { ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./TruncateTooltip.module.scss";
import { TooltipAnchor, useTruncateTooltipPosition } from "./useTruncateTooltipPosition";

export interface TruncateTooltipProps {
  text: string;
  // Extra class(es) for the truncating text element itself (typography/color
  // come from the call site, truncation + hover detection from here).
  className?: string;
  // Overrides what's rendered inside the tooltip popup — use this to preserve
  // a cell's original visual hierarchy (e.g. bold primary + gray secondary
  // line) instead of a single flat line. Falls back to `text`.
  tooltipContent?: ReactNode;
  // "auto" (default) prefers above the text and only drops below if there's
  // no room. "below" always opens below, e.g. for header cells where "above"
  // would land outside the page or overlap unrelated content.
  placement?: "auto" | "below";
}

// Single-line ellipsis text that shows a dark hover tooltip with the full
// value — but ONLY when the text is actually truncated (scrollWidth exceeds
// what's visible). Rendered through a portal so table/scroll containers with
// overflow: hidden can't clip it.
export default function TruncateTooltip({ text, className, tooltipContent, placement = "auto" }: TruncateTooltipProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<TooltipAnchor | null>(null);

  const handleMouseEnter = () => {
    const el = textRef.current;
    // Not truncated → no tooltip. -1 tolerates sub-pixel rounding.
    if (!el || el.scrollWidth - el.clientWidth <= 1) return;
    const rect = el.getBoundingClientRect();
    setAnchor({ top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width });
  };

  const handleMouseLeave = () => setAnchor(null);

  const position = useTruncateTooltipPosition(anchor, tipRef, placement);

  return (
    <span
      ref={textRef}
      className={`${styles.truncateText} ${className || ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {text}
      {anchor &&
        createPortal(
          <div
            ref={tipRef}
            className={`${styles.truncateTooltip} ${position?.below ? styles.below : ""}`}
            style={{
              top: position?.top ?? anchor.top,
              left: position?.left ?? anchor.left,
              visibility: position ? "visible" : "hidden",
            }}
          >
            {tooltipContent ?? text}
          </div>,
          document.body
        )}
    </span>
  );
}
